<template>
  <div class="domains-view">
    <!-- Full page loading overlay -->
    <div v-if="isAnyDomainToggling" class="full-page-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <div class="loading-text">Enabling site</div>
      </div>
    </div>

    <!-- Subscription limit modal -->
    <div v-if="showLimitModal" class="modal-overlay" @click.self="showLimitModal = false">
      <div class="modal-content">
        <button class="modal-close" @click="showLimitModal = false">&times;</button>
        <div class="modal-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <h3 class="modal-title">Site Limit Reached</h3>
        <p class="modal-message">You've reached the maximum number of sites allowed on your current plan. Upgrade your subscription to add more sites.</p>
        <div class="modal-actions">
          <button class="modal-btn secondary" @click="showLimitModal = false">Cancel</button>
          <button class="modal-btn primary" @click="goToSubscriptions">Upgrade Plan</button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal-content delete-modal">
        <button class="modal-close" @click="cancelDelete">&times;</button>
        <div class="modal-icon danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18"></path>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            <line x1="10" y1="11" x2="10" y2="17"></line>
            <line x1="14" y1="11" x2="14" y2="17"></line>
          </svg>
        </div>
        <h3 class="modal-title">Delete Site</h3>
        <p class="modal-message">
          You are about to delete <strong>{{ domainToDelete?.name }}</strong>
        </p>
        <div class="delete-warning">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>Deleting a site is irreversible and deletes all history.</span>
        </div>
        <div class="delete-confirm-input">
          <label>Type <strong>DELETE</strong> to confirm:</label>
          <input 
            type="text" 
            v-model="deleteConfirmText" 
            placeholder="DELETE"
            @keyup.enter="confirmDelete"
            autofocus
          />
        </div>
        <div class="modal-actions">
          <button class="modal-btn secondary" @click="cancelDelete" :disabled="isDeleting">Cancel</button>
          <button 
            class="modal-btn danger" 
            @click="confirmDelete" 
            :disabled="deleteConfirmText !== 'DELETE' || isDeleting"
          >
            <span v-if="isDeleting">Deleting...</span>
            <span v-else>Delete Site</span>
          </button>
        </div>
      </div>
    </div>
    
    <div v-if="$route.query.error" class="error-toast">
      {{ $route.query.error }}
    </div>
    <div v-if="toggleError" class="error-toast">
      {{ toggleError }}
    </div>
    <div v-if="successMessage" class="success-toast">
      {{ successMessage }}
    </div>

    <!-- Scheduled Cancellation Notification -->
    <div v-if="hasScheduledCancellation" class="cancellation-notification">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <div class="notification-content">
        <strong>Subscription scheduled to cancel</strong>
        <span>Your subscription will be cancelled on {{ formatCancellationDate() }}. <a href="#" @click.prevent="manageSubscription" class="notification-link">Don't cancel</a></span>
      </div>
    </div>

    <!-- Scheduled Downgrade Notification -->
    <div v-if="showDowngradeNotification" class="downgrade-notification">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <polyline points="19 12 12 19 5 12"></polyline>
      </svg>
      <div class="notification-content">
        <strong>Downgrade scheduled</strong>
        <span>Your subscription will be downgraded<template v-if="scheduledDowngradePlanName"> to <strong>{{ scheduledDowngradePlanName }}</strong></template> on {{ formatScheduledChangeDate() }}. <a href="#" @click.prevent="goToSubscriptions" class="notification-link">Manage subscription</a></span>
      </div>
      <button class="notification-close" @click="dismissDowngradeNotification" title="Dismiss">&times;</button>
    </div>

    <!-- Site Limit Exceeded Warning -->
    <div v-if="exceededSiteLimit" class="site-limit-warning">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
      <div class="notification-content">
        <strong>Site limit exceeded</strong>
        <span>Your current plan supports <strong>{{ planSiteLimit }}</strong> {{ planSiteLimit === 1 ? 'site' : 'sites' }}, but you have <strong>{{ domainsCount }}</strong>. All sites are paused and will not be processed for scanning. <a href="#" @click.prevent="goToSubscriptions" class="notification-link">Upgrade plan</a> or <b>delete</b> sites to re-enable scanning to your sites. <em>Deleting a site will remove all scan history.</em></span>
      </div>
    </div>

    <div class="header">
      <h2>Sites</h2>
      <div class="header-controls">
        <div class="search-container">
          <div class="search-input-wrapper">
            <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
            </svg>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search sites"
              class="search-input"
            />
          </div>
        </div>
        <button class="create-site-button" @click="handleCreateSite">
          Create Site
          <span class="plus">+</span>
        </button>
      </div>
    </div>
    <div v-if="loading" class="loading">
      Loading domains...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    <div v-else-if="domains && domains.length === 0" class="no-domains">
      No sites found.
    </div>
    <div v-else-if="filteredDomains && filteredDomains.length === 0 && searchQuery.trim()" class="no-domains">
      No sites match your search.
    </div>
    <div v-else-if="domains && domains.length" class="domains-list">
      <div class="domains-header">
        <div class="domain-item sortable" @click="sortColumn('name')">
          Name
          <span class="sort-indicator" v-if="sortBy === 'name'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
        </div>
        <div class="domain-item desktop-only">URL</div>
        <div class="domain-item desktop-only">Enabled</div>
        <div class="domain-item desktop-only numeric">Scanned nodes</div>
        <div class="domain-item desktop-only">Created At</div>
        <div class="domain-item desktop-only sortable" @click="sortColumn('lastScanAt')">
          Last scanned
          <span class="sort-indicator" v-if="sortBy === 'lastScanAt'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
        </div>
        <div class="domain-item">Changes</div>
        <div class="domain-item">AI Review</div>
        <div class="domain-item"></div>
      </div>
      <div v-for="domain in filteredDomains" :key="domain.id" 
           :class="['domain-row', isDefaultScanDate(domain.lastScanAt) ? '' : 'clickable']"
           @click="!isDefaultScanDate(domain.lastScanAt) && navigateToScanResults(domain)"
           :title="isDefaultScanDate(domain.lastScanAt) ? 'No scan data available' : 'Click to view latest changes'"
           :style="isDefaultScanDate(domain.lastScanAt) ? 'cursor: default;' : 'cursor: pointer;'">
        <div class="domain-item name-cell">
          <span class="domain-name">{{ domain.name }}</span>
          <div class="mobile-enabled-toggle">
            <span class="mobile-enabled-label">Enabled:</span>
            <div 
              class="toggle-switch"
              :class="{ disabled: domain.toggling }"
              @click.stop="toggleDomainStatus(domain)"
            >
              <div 
                class="toggle-track"
                :class="{ 'track-enabled': domain.isEnabled, 'track-disabled': !domain.isEnabled }"
              >
                <div 
                  class="toggle-knob"
                  :class="{ 'knob-enabled': domain.isEnabled, 'knob-disabled': !domain.isEnabled }"
                >
                  <div class="toggle-icon" v-if="!domain.toggling">
                    <svg v-if="domain.isEnabled" viewBox="0 0 12 12" width="8" height="8">
                      <path d="M2 6l2.5 2.5L10 3" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else viewBox="0 0 12 12" width="8" height="8">
                      <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                  </div>
                  <div class="toggle-spinner" v-if="domain.toggling">
                    <div class="spinner"></div>
                  </div>
                </div>
                <div class="toggle-overlay" v-if="domain.toggling"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="domain-item desktop-only">{{ domain.url }}</div>
        <div class="domain-item desktop-only">
          <div 
            class="toggle-switch"
            :class="{ disabled: domain.toggling }"
            @click.stop="toggleDomainStatus(domain)"
          >
            <div 
              class="toggle-track"
              :class="{ 'track-enabled': domain.isEnabled, 'track-disabled': !domain.isEnabled }"
            >
              <div class="toggle-text">
                <span class="text-enabled" :class="{ active: domain.isEnabled }">ON</span>
                <span class="text-disabled" :class="{ active: !domain.isEnabled }">OFF</span>
              </div>
              <div 
                class="toggle-knob"
                :class="{ 'knob-enabled': domain.isEnabled, 'knob-disabled': !domain.isEnabled }"
              >
                <div class="toggle-icon" v-if="!domain.toggling">
                  <svg v-if="domain.isEnabled" viewBox="0 0 12 12" width="8" height="8">
                    <path d="M2 6l2.5 2.5L10 3" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else viewBox="0 0 12 12" width="8" height="8">
                    <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </div>
                <div class="toggle-spinner" v-if="domain.toggling">
                  <div class="spinner"></div>
                </div>
              </div>
              <div class="toggle-overlay" v-if="domain.toggling"></div>
            </div>
          </div>
        </div>
        <div class="domain-item desktop-only numeric">{{ getNodesDisplay(domain) }}</div>
        <div class="domain-item desktop-only">
          <div class="date-time">
            <div class="date">{{ formatDate(domain.createdAt) }}</div>
            <div class="time">{{ formatTime(domain.createdAt) }}</div>
          </div>
        </div>
        <div class="domain-item desktop-only">
          <div v-if="isDefaultScanDate(domain.lastScanAt)" class="na-status">N/A</div>
          <div v-else class="date-time">
            <div class="date">{{ formatDate(domain.lastScanAt) }}</div>
            <div class="time">{{ formatTime(domain.lastScanAt) }}</div>
          </div>
        </div>
        <div class="domain-item">
          <span v-if="isDefaultScanDate(domain.lastScanAt)" class="na-status">N/A</span>
          <span v-else-if="domain.hasChanges" class="changes-badge has-changes" title="Changes detected">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </span>
          <span v-else class="changes-badge no-changes" title="No changes">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8 12l2.5 2.5L16 9"/>
            </svg>
          </span>
        </div>
        <div class="domain-item">
          <span v-if="isDefaultScanDate(domain.lastScanAt)" class="na-status">N/A</span>
          <span v-else :class="['ai-review-badge', getAiReviewType(domain)]" :title="getAiReviewLabel(domain)">
            <!-- Safe: shield with checkmark -->
            <template v-if="getAiReviewType(domain) === 'safe'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
            </template>
            <!-- Suspicious: warning triangle -->
            <template v-else-if="getAiReviewType(domain) === 'suspicious'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </template>
            <!-- Malicious: shield with X -->
            <template v-else-if="getAiReviewType(domain) === 'malicious'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
              </svg>
            </template>
            <!-- Pending -->
            <template v-else>
              <span class="ai-review-pending">—</span>
            </template>
          </span>
        </div>
        <div class="domain-item">
          <div class="actions-container">
            <button
              class="edit-button desktop-only"
              @click.stop="navigateToDomainDetails(domain)"
              title="Edit domain">
              <img src="../assets/trailing-icon.png" alt="Edit" class="edit-icon">
            </button>
            <div class="dropdown-container">
              <button
                class="more-actions-button"
                @click.stop="toggleDropdown(domain.id)"
                title="More actions">
                <svg class="more-icon" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2"/>
                  <circle cx="12" cy="12" r="2"/>
                  <circle cx="12" cy="19" r="2"/>
                </svg>
              </button>
              <div
                v-if="openDropdown === domain.id"
                class="dropdown-menu"
                @click.stop>
                <button
                  class="dropdown-item mobile-only-item"
                  @click="navigateToDomainDetails(domain)">
                  Edit
                </button>
                <button
                  class="dropdown-item delete-item"
                  @click="deleteDomain(domain)">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api, getCurrentUser } from '../utils/api'

export default {
  name: 'DomainsView',
  inject: ['env'],
  data() {
    return {
      domains: [],
      userInfo: null,
      loading: true,
      error: null,
      toggleError: null,
      successMessage: null,
      searchQuery: '',
      openDropdown: null,
      sortBy: null,
      sortOrder: 'asc',
      showLimitModal: false,
      subscriptionPlans: [],
      downgradeNotificationDismissed: false,
      showDeleteModal: false,
      domainToDelete: null,
      deleteConfirmText: '',
      isDeleting: false
    }
  },
  computed: {
    isAnyDomainToggling() {
      return this.domains.some(domain => domain.toggling);
    },
    scheduledCancellation() {
      const changes = this.userInfo?.subscription?.scheduledChanges
      if (!Array.isArray(changes)) return null
      return changes.find(c => c.action === 'cancel') || null
    },
    hasScheduledCancellation() {
      return this.scheduledCancellation !== null
    },
    scheduledDowngrade() {
      const changes = this.userInfo?.subscription?.scheduledChanges
      if (!Array.isArray(changes)) return null
      return changes.find(c => c.action === 'downgrade') || null
    },
    hasScheduledDowngrade() {
      return this.scheduledDowngrade !== null
    },
    scheduledDowngradePlanName() {
      if (!this.scheduledDowngrade) return null
      const newPlanId = this.scheduledDowngrade.newPlanId
      const plan = this.subscriptionPlans.find(p => p.id === newPlanId)
      if (!plan) return null
      // Remove "Yearly" suffix if present
      if (plan.name.endsWith(' Yearly')) {
        return plan.name.replace(' Yearly', '')
      }
      return plan.name
    },
    downgradeNotificationKey() {
      // Create a unique key based on the scheduled change to allow new notifications
      if (!this.scheduledDowngrade) return null
      const effectiveAt = this.scheduledDowngrade.effectiveAt
      return `downgrade_notification_dismissed_${effectiveAt}`
    },
    showDowngradeNotification() {
      return this.hasScheduledDowngrade && !this.downgradeNotificationDismissed
    },
    planSiteLimit() {
      return this.userInfo?.subscription?.siteCount || 0
    },
    domainsCount() {
      return this.domains?.length || 0
    },
    exceededSiteLimit() {
      // Admins bypass site limits
      if (this.userInfo?.isAdmin) {
        return false
      }
      return this.planSiteLimit > 0 && this.domainsCount > this.planSiteLimit
    },
    excessSitesCount() {
      return this.domainsCount - this.planSiteLimit
    },
    filteredDomains() {
      let filtered = this.domains;

      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(domain =>
          domain.name?.toLowerCase().includes(query) ||
          domain.url?.toLowerCase().includes(query)
        );
      }

      if (this.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          let aVal, bVal;

          if (this.sortBy === 'name') {
            aVal = a.name?.toLowerCase() || '';
            bVal = b.name?.toLowerCase() || '';
          } else if (this.sortBy === 'lastScanAt') {
            aVal = new Date(a.lastScanAt).getTime();
            bVal = new Date(b.lastScanAt).getTime();
          }

          if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return filtered;
    }
  },
  watch: {
    '$route.query.error': {
      immediate: true,
      handler(error) {
        if (error) {
          // Clear the error from the URL after displaying it
          this.$router.replace({ query: {} })
        }
      }
    },
    '$route.query.deleted': {
      immediate: true,
      handler(deleted) {
        if (deleted) {
          this.showDeleteSuccess()
          const query = { ...this.$route.query }
          delete query.deleted
          this.$router.replace({ query })
        }
      }
    }
  },
  async created() {
    await Promise.all([this.fetchDomains(), this.fetchCurrentUser(), this.fetchSubscriptionPlans()])
    this.checkDismissedNotification()
  },
  mounted() {
    document.addEventListener('click', this.closeDropdown);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdown);
    if (this.deleteSuccessTimeout) {
      clearTimeout(this.deleteSuccessTimeout)
    }
  },
  methods: {
    async fetchDomains() {
      try {
        // Use the centralized API utility
        const data = await api('/domains', {}, this.$router)
        if (Array.isArray(data)) {
          this.domains = data
        } else if (data && Array.isArray(data.domains)) {
          this.domains = data.domains
        } else {
          this.domains = []
        }
      } catch (error) {
        // Use code from error response if available, otherwise use default message
        this.error = error.code || 'Something went wrong please contact support'
      } finally {
        this.loading = false
      }
    },

    async fetchCurrentUser() {
      try {
        this.userInfo = await getCurrentUser(this.$router)
      } catch (error) {
        console.error('Failed to fetch user info:', error)
      }
    },

    async fetchSubscriptionPlans() {
      try {
        const data = await api('/subscription-plans', {}, this.$router)
        this.subscriptionPlans = data || []
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error)
      }
    },

    checkDismissedNotification() {
      if (this.downgradeNotificationKey) {
        try {
          this.downgradeNotificationDismissed = localStorage.getItem(this.downgradeNotificationKey) === 'true'
        } catch (e) {
          // localStorage not available (private browsing)
          this.downgradeNotificationDismissed = false
        }
      }
    },

    dismissDowngradeNotification() {
      this.downgradeNotificationDismissed = true
      if (this.downgradeNotificationKey) {
        try {
          localStorage.setItem(this.downgradeNotificationKey, 'true')
        } catch (e) {
          // localStorage not available (private browsing)
        }
      }
    },

    formatCancellationDate() {
      if (!this.scheduledCancellation) return ''
      const date = new Date(this.scheduledCancellation.effectiveAt)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatScheduledChangeDate() {
      if (!this.scheduledDowngrade?.effectiveAt) return ''
      const date = new Date(this.scheduledDowngrade.effectiveAt)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    async manageSubscription() {
      try {
        const response = await api('/billing/customer-portal', {}, this.$router)
        
        // Open the portal link in a new tab
        if (response && response.link) {
          window.open(response.link, '_blank')
        } else {
          console.error('No portal link returned from API')
        }
      } catch (error) {
        console.error('Error opening customer portal:', error)
      }
    },

    handleCreateSite() {
      // Admins bypass subscription limits
      if (this.userInfo?.isAdmin) {
        this.$router.push('/domains/create')
        return
      }

      const domainsCount = this.domains?.length || 0
      const siteLimit = this.userInfo?.subscription?.siteCount || 0

      if (domainsCount >= siteLimit) {
        this.showLimitModal = true
      } else {
        this.$router.push('/domains/create')
      }
    },

    goToSubscriptions() {
      this.showLimitModal = false
      this.$router.push('/subscriptions')
    },

    navigateToScanResults(domain) {
      this.$router.push({
        name: 'scan-results',
        params: { id: domain.id }
      })
    },

    navigateToDomainDetails(domain) {
      this.$router.push({
        name: 'DomainDetail',
        params: { id: domain.id }
      })
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    },

    formatTime(dateString) {
      const date = new Date(dateString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    },

    formatLastScanned(lastScanAt) {
      const date = new Date(lastScanAt);
      const defaultDate = new Date('0001-01-01T00:00:00Z');
      
      if (date.getTime() === defaultDate.getTime()) {
        return 'N/A';
      }
      
      return date.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' });
    },

    isDefaultScanDate(lastScanAt) {
      const date = new Date(lastScanAt);
      const defaultDate = new Date('0001-01-01T00:00:00Z');
      return date.getTime() === defaultDate.getTime();
    },

    formatChanges(results) {
      if (!results) return null;
      
      const changes = {
        added: Object.entries(results.added || {}).map(([path, details]) => ({
          path,
          type: details.type,
          size: details.size,
          modTime: new Date(details.modTime * 1000).toLocaleString()
        })),
        removed: Object.entries(results.removed || {}).map(([path, details]) => ({
          path,
          type: details.type,
          size: details.size,
          modTime: new Date(details.modTime * 1000).toLocaleString()
        })),
        changed: Object.entries(results.changed || {}).map(([path, details]) => ({
          path,
          type: details.type,
          size: details.size,
          modTime: new Date(details.modTime * 1000).toLocaleString()
        }))
      };

      return {
        ...changes,
        hasChanges: changes.added.length > 0 || changes.removed.length > 0 || changes.changed.length > 0
      };
    },

    getNodesDisplay(domain) {
      // nodesScanned is included directly in the domain object from the API
      const lastScanned = domain.nodesScanned ?? domain.scanResults?.nodesScanned;

      if (lastScanned !== undefined && lastScanned !== null) {
        return lastScanned.toLocaleString('de-DE');
      }
      return 'N/A';
    },

    async toggleDomainStatus(domain) {
      if (domain.toggling) return; // Prevent multiple clicks

      domain.toggling = true;
      try {
        const newEnableState = !domain.isEnabled;
        await api(`/domains/${domain.id}/enable`, {
          method: 'POST',
          body: JSON.stringify({ enable: newEnableState })
        }, this.$router);

        domain.isEnabled = newEnableState;
      } catch (error) {
        // Use code from error response if available, otherwise use default message
        this.toggleError = error.code || 'Something went wrong please contact support';

        // Auto-hide error after 5 seconds
        setTimeout(() => {
          this.toggleError = null;
        }, 5000);
      } finally {
        domain.toggling = false;
      }
    },

    toggleDropdown(domainId) {
      this.openDropdown = this.openDropdown === domainId ? null : domainId;
    },

    closeDropdown(event) {
      // Don't close if clicking on the dropdown button or menu itself
      if (event && (
        event.target.closest('.more-actions-button') ||
        event.target.closest('.dropdown-menu')
      )) {
        return;
      }
      this.openDropdown = null;
    },

    deleteDomain(domain) {
      this.domainToDelete = domain;
      this.deleteConfirmText = '';
      this.showDeleteModal = true;
      this.openDropdown = null;
    },

    showDeleteSuccess() {
      this.successMessage = 'Domain deleted successfully'
      if (this.deleteSuccessTimeout) {
        clearTimeout(this.deleteSuccessTimeout)
      }
      this.deleteSuccessTimeout = setTimeout(() => {
        this.successMessage = null
      }, 5000)
    },

    cancelDelete() {
      this.showDeleteModal = false;
      this.domainToDelete = null;
      this.deleteConfirmText = '';
    },

    async confirmDelete() {
      if (this.deleteConfirmText !== 'DELETE' || !this.domainToDelete) {
        return;
      }

      this.isDeleting = true;

      try {
        await api(`/domains/${this.domainToDelete.id}`, {
          method: 'DELETE'
        }, this.$router);

        // Remove domain from the list
        const index = this.domains.findIndex(d => d.id === this.domainToDelete.id);
        if (index > -1) {
          this.domains.splice(index, 1);
        }

        this.showDeleteSuccess()

        // Close modal and reset state
        this.showDeleteModal = false;
        this.domainToDelete = null;
        this.deleteConfirmText = '';
      } catch (error) {
        // Use code from error response if available, otherwise use default message
        this.toggleError = error.code || 'Something went wrong please contact support';

        // Auto-hide error after 5 seconds
        setTimeout(() => {
          this.toggleError = null;
        }, 5000);

        // Close modal on error too
        this.showDeleteModal = false;
        this.domainToDelete = null;
        this.deleteConfirmText = '';
      } finally {
        this.isDeleting = false;
      }
    },

    sortColumn(column) {
      if (this.sortBy === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        this.sortOrder = 'asc';
      }
    },

    isSafeWithChanges(domain) {
      const hasChanges = domain.hasChanges || false;
      const aiStatus = domain.aiStatus;
      const heuristicStatus = domain.heuristicStatus;

      // Special case: hasChanges && (aiStatus === 'unset' && heuristicStatus IN ('Most probably safe', 'Probably safe'))
      return hasChanges && 
             aiStatus === 'unset' && 
             (heuristicStatus === 'Most probably safe' || heuristicStatus === 'Probably safe');
    },

    getScanStatusType(domain) {
      const hasChanges = domain.hasChanges || false;
      const aiStatus = domain.aiStatus;
      const heuristicStatus = domain.heuristicStatus;

      // Critical/Malicious: hasChanges && (aiStatus === 'malicious' || (aiStatus === 'unset' && heuristicStatus === 'Malicious'))
      if (hasChanges && (
        aiStatus === 'malicious' || 
        (aiStatus === 'unset' && heuristicStatus === 'Malicious')
      )) {
        return 'critical';
      }

      // Warning: hasChanges && (aiStatus === 'suspicious' || (aiStatus === 'unset' && heuristicStatus in ('Suspicious', 'Highly suspicious')))
      if (hasChanges && (
        aiStatus === 'suspicious' || 
        (aiStatus === 'unset' && (heuristicStatus === 'Suspicious' || heuristicStatus === 'Highly suspicious'))
      )) {
        return 'warning';
      }

      // Safe: !hasChanges || aiStatus === 'safe' || (aiStatus === 'unset' && heuristicStatus in ('Most probably safe', 'Probably safe'))
      // Note: Special case of hasChanges with safe heuristic is handled separately in template via isSafeWithChanges
      if (!hasChanges || 
          aiStatus === 'safe' || 
          (aiStatus === 'unset' && (heuristicStatus === 'Most probably safe' || heuristicStatus === 'Probably safe'))) {
        return 'safe';
      }

      // Default to disabled
      return 'disabled';
    },

    getAiReviewType(domain) {
      const aiStatus = domain.aiStatus;
      const hasChanges = domain.hasChanges || false;

      if (aiStatus === 'safe') return 'safe';
      if (aiStatus === 'suspicious') return 'suspicious';
      if (aiStatus === 'malicious') return 'malicious';
      if (aiStatus === 'unset' && !hasChanges) return 'safe';

      return 'pending';
    },

    getAiReviewLabel(domain) {
      const type = this.getAiReviewType(domain);
      if (type === 'safe') return 'Safe';
      if (type === 'suspicious') return 'Suspicious';
      if (type === 'malicious') return 'Malicious';
      return 'Pending review';
    },

    getScanStatusClass(domain) {
      const statusType = this.getScanStatusType(domain);
      
      if (statusType === 'critical') {
        return 'critical';
      } else if (statusType === 'warning') {
        return 'warning';
      } else if (statusType === 'safe' || this.isSafeWithChanges(domain)) {
        return 'success';
      } else {
        return 'disabled';
      }
    }
  }
}
</script>

<style scoped>
.domains-view {
  padding: 20px 0;
  margin: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-container {
  display: flex;
  align-items: center;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 20px;
  height: 20px;
  color: white;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 240px;
  height: 38px;
  padding: 0 12px 0 42px;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 16px;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
  color: var(--text-muted);
}

.create-site-button {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: var(--accent-color);
  color: white;
  text-decoration: none;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s;
  white-space: nowrap;
  cursor: pointer;
}

.create-site-button:hover {
  background-color: var(--accent-hover);
}

.plus {
  font-size: 1.2em;
  margin-left: 8px;
}

.loading, .error, .no-domains {
  text-align: center;
  padding: 20px;
  font-size: 1.2em;
  color: var(--text-primary);
}

.error {
  color: var(--error-color);
}

.domains-list {
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  margin-top: 20px;
  border: 1px solid var(--border-color);
}

.domains-header {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr 48px;
  padding: 15px 20px;
  background: var(--bg-tertiary);
  border-bottom: 2px solid var(--border-color);
  font-weight: bold;
  color: var(--text-primary);
  font-size: 14px;
}

.domain-row {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr 48px;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  color: var(--text-primary);
  transition: background-color 0.2s ease;
  font-size: 14px;
}

.domain-row:last-child {
  border-bottom: none;
}

.domain-row.clickable:hover {
  background-color: rgba(0, 123, 255, 0.1);
  cursor: pointer;
}

.domain-row:not(.clickable):hover {
  background-color: transparent;
  cursor: default;
}

.domain-item {
  padding: 0 10px;
  display: flex;
  align-items: center;
}

.domain-item.numeric {
  justify-content: flex-end;
  text-align: left;
}


.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 500;
}

.status-badge.enabled {
  background-color: var(--success-bg);
  color: var(--success-color);
}

.status-badge.disabled {
  background-color: var(--error-bg);
  color: var(--error-color);
}

.toggle-switch {
  cursor: pointer;
  user-select: none;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.toggle-track {
  position: relative;
  width: 80px;
  height: 24px;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  overflow: hidden;
}

.track-disabled {
  background-color: var(--error-bg);
  border: 1px solid var(--error-color);
}

.track-enabled {
  background-color: var(--success-bg);
  border: 1px solid var(--success-color);
}

.toggle-switch:not(.disabled):hover .track-disabled {
  background-color: var(--error-color);
}

.toggle-switch:not(.disabled):hover .track-enabled {
  background-color: var(--success-color);
}

.toggle-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  z-index: 1;
}

.text-enabled {
  transition: opacity 0.2s ease;
  position: absolute;
  left: 8px;
  text-align: left;
}

.text-disabled {
  transition: opacity 0.2s ease;
  position: absolute;
  right: 8px;
  text-align: right;
}

.text-enabled.active {
  opacity: 1;
  color: white;
}

.text-disabled.active {
  opacity: 1;
  color: white;
}

.text-enabled:not(.active) {
  opacity: 0;
}

.text-disabled:not(.active) {
  opacity: 0;
}

.toggle-knob {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 20px;
  height: 20px;
  background-color: white;
  border: 1px solid var(--border-color);
  border-radius: 50%;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.knob-enabled {
  transform: translateX(58px);
}

.knob-disabled {
  transform: translateX(0);
}

.toggle-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.toggle-switch:not(.disabled) .knob-enabled .toggle-icon {
  color: var(--success-color);
}

.toggle-switch:not(.disabled) .knob-disabled .toggle-icon {
  color: var(--error-color);
}

.toggle-switch.disabled .toggle-icon {
  color: var(--text-muted);
}

.toggle-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.toggle-spinner .spinner {
  width: 10px;
  height: 10px;
  border: 1px solid var(--text-muted);
  border-top: 1px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.toggle-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  z-index: 3;
  pointer-events: none;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.full-page-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--bg-secondary);
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
}

.loading-spinner .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 500;
}

.domain-name {
  color: white;
  font-weight: 500;
}

.na-status {
  color: var(--text-muted);
  font-style: italic;
}

.status-icon {
  width: 24px;
  height: 24px;
  vertical-align: middle;
}

.date-time {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  gap: 6px;
}

.date {
  font-weight: 500;
  color: var(--text-primary);
}

.time {
  font-size: 14px;
  color: var(--text-muted);
}

.error-toast {
  background-color: var(--error-color);
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: left;
  animation: slideIn 0.3s ease-out;
}

.success-toast {
  background-color: var(--success-color);
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: left;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Cancellation Notification */
.cancellation-notification {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  animation: slideIn 0.4s ease-out;
}

.cancellation-notification svg {
  color: #ef4444;
  flex-shrink: 0;
}

.cancellation-notification .notification-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.cancellation-notification .notification-content strong {
  color: #ef4444;
  font-size: 14px;
  font-weight: 600;
}

.cancellation-notification .notification-content span {
  color: var(--text-muted);
  font-size: 13px;
}

.cancellation-notification .notification-link {
  color: #ef4444;
  text-decoration: underline;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.cancellation-notification .notification-link:hover {
  color: #dc2626;
}

/* Downgrade Notification */
.downgrade-notification {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  animation: slideIn 0.4s ease-out;
}

.downgrade-notification svg {
  color: #f59e0b;
  flex-shrink: 0;
}

.downgrade-notification .notification-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.downgrade-notification .notification-content strong {
  color: #f59e0b;
  font-size: 14px;
  font-weight: 600;
}

.downgrade-notification .notification-content span {
  color: var(--text-muted);
  font-size: 13px;
}

.downgrade-notification .notification-content span strong {
  color: var(--text-primary);
}

.notification-link {
  color: #f59e0b;
  text-decoration: underline;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.notification-link:hover {
  color: #d97706;
}

.notification-close {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 24px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.notification-close:hover {
  color: var(--text-primary);
}

/* Site Limit Warning */
.site-limit-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  animation: slideIn 0.4s ease-out;
}

.site-limit-warning svg {
  color: #ef4444;
  flex-shrink: 0;
  margin-top: 2px;
}

.site-limit-warning .notification-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.site-limit-warning .notification-content strong {
  color: #ef4444;
  font-size: 14px;
  font-weight: 600;
}

.site-limit-warning .notification-content span {
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.5;
}

.site-limit-warning .notification-content span strong {
  color: var(--text-primary);
}

.site-limit-warning .notification-link {
  color: #ef4444;
}

.site-limit-warning .notification-link:hover {
  color: #dc2626;
}

.changes-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.changes-badge svg {
  width: 22px;
  height: 22px;
}

.changes-badge.has-changes {
  color: #f59e0b;
}

.changes-badge.no-changes {
  color: var(--success-color);
}

.changes-badge:hover {
  transform: scale(1.1);
}

.ai-review-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.ai-review-badge svg {
  width: 22px;
  height: 22px;
}

.ai-review-badge.safe {
  color: var(--success-color);
}

.ai-review-badge.suspicious {
  color: #f59e0b;
}

.ai-review-badge.malicious {
  color: #ef4444;
}

.ai-review-badge:hover {
  transform: scale(1.1);
}

.ai-review-pending {
  color: var(--text-muted);
  font-size: 14px;
}

.actions-container {
  display: flex;
  align-items: center;
  gap: 4px;
}

.edit-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  color: var(--text-muted);
}

.edit-icon {
  width: 16px;
  height: 16px;
  vertical-align: middle;
}

.edit-button:hover {
  background-color: var(--bg-tertiary);
  color: var(--accent-color);
}

.dropdown-container {
  position: relative;
}

.more-actions-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
}

.more-icon {
  width: 16px;
  height: 16px;
}

.more-actions-button:hover {
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 120px;
  overflow: hidden;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.dropdown-item:hover {
  background-color: var(--bg-tertiary);
}

.dropdown-item.delete-item {
  color: var(--error-color);
}

.dropdown-item.delete-item:hover {
  background-color: var(--error-bg);
  color: var(--error-color);
}

.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;
}

.sortable:hover {
  color: var(--accent-color);
}

.sort-indicator {
  margin-left: 4px;
  font-size: 10px;
  color: var(--accent-color);
}


/* Desktop-only elements */
.desktop-only {
  display: block;
}

.mobile-enabled-toggle {
  display: none;
}

.mobile-only-item {
  display: none;
}

/* Mobile responsive design */
@media (max-width: 768px) {
  .domains-view {
    padding: 20px 0;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-enabled-toggle {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
  }

  .mobile-enabled-label {
    font-size: 11px;
    color: var(--text-muted);
    white-space: nowrap;
  }

  .mobile-enabled-toggle .toggle-switch {
    transform: scale(0.75);
    transform-origin: left center;
  }

  .mobile-enabled-toggle .toggle-track {
    width: 40px;
    height: 20px;
  }

  .mobile-enabled-toggle .toggle-knob {
    width: 16px;
    height: 16px;
  }

  .mobile-enabled-toggle .knob-enabled {
    transform: translateX(22px);
  }

  .mobile-enabled-toggle .toggle-text {
    display: none;
  }

  .name-cell {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .mobile-only-item {
    display: block;
  }

  .header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .header-controls {
    flex-direction: column;
    gap: 12px;
  }

  .search-input {
    width: 100%;
  }

  .create-site-button {
    justify-content: center;
  }

  .domains-header {
    grid-template-columns: 3fr 1fr 1fr 36px;
    font-size: 12px;
    padding: 12px 15px;
  }

  .domain-row {
    grid-template-columns: 3fr 1fr 1fr 36px;
    padding: 12px 15px;
  }

  .domain-row .domain-item:first-child {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .domain-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
    width: 100%;
    font-size: 14px;
  }

  .toggle-switch {
    transform: scale(0.8);
  }

  /* Hide toggle text on mobile, show only icons */
  .toggle-text {
    display: none;
  }

  .toggle-track {
    width: 40px;
    height: 20px;
  }

  .toggle-knob {
    width: 16px;
    height: 16px;
  }

  .knob-enabled {
    transform: translateX(22px);
  }

  .toggle-icon {
    transform: scale(0.9);
  }

  .cancellation-notification {
    padding: 12px 16px;
    gap: 10px;
  }

  .cancellation-notification .notification-content strong {
    font-size: 13px;
  }

  .cancellation-notification .notification-content span {
    font-size: 12px;
  }

  .downgrade-notification {
    padding: 12px 16px;
    gap: 10px;
  }

  .downgrade-notification .notification-content strong {
    font-size: 13px;
  }

  .downgrade-notification .notification-content span {
    font-size: 12px;
  }

  .site-limit-warning {
    padding: 12px 16px;
    gap: 10px;
  }

  .site-limit-warning .notification-content strong {
    font-size: 13px;
  }

  .site-limit-warning .notification-content span {
    font-size: 12px;
  }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 32px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  position: relative;
  text-align: center;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 24px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  transition: color 0.2s ease;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  color: var(--warning-color, #f59e0b);
}

.modal-icon svg {
  width: 100%;
  height: 100%;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 12px;
}

.modal-message {
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.6;
  margin: 0 0 24px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.modal-btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.modal-btn.primary {
  background-color: var(--accent-color);
  color: white;
}

.modal-btn.primary:hover {
  background-color: var(--accent-hover);
}

.modal-btn.secondary {
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.modal-btn.secondary:hover {
  background-color: var(--border-color);
}

.modal-btn.danger {
  background-color: #ef4444;
  color: white;
}

.modal-btn.danger:hover:not(:disabled) {
  background-color: #dc2626;
}

.modal-btn.danger:disabled {
  background-color: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Delete modal specific styles */
.delete-modal .modal-icon.danger {
  color: #ef4444;
}

.delete-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.delete-warning svg {
  color: #ef4444;
  flex-shrink: 0;
}

.delete-warning span {
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.4;
}

.delete-confirm-input {
  margin-bottom: 24px;
  text-align: left;
}

.delete-confirm-input label {
  display: block;
  font-size: 13px;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.delete-confirm-input label strong {
  color: var(--text-primary);
  font-family: monospace;
  background: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: 4px;
}

.delete-confirm-input input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-size: 14px;
  font-family: monospace;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.delete-confirm-input input:focus {
  outline: none;
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}

.delete-confirm-input input::placeholder {
  color: var(--text-muted);
  opacity: 0.5;
}

@media (max-width: 768px) {
  .modal-content {
    padding: 24px;
    margin: 16px;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-btn {
    width: 100%;
  }
}
</style>
